import { GuardedDeletionService } from 'projects/orchestrator/src/lib/services/guarded-deletion.service';
import { OnInit, Component, inject } from '@angular/core';
import { ListService } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import {
  WoodCertificationCreateDto,
  WoodCertificationUpdateDto,
} from '../proxy/geographic/dtos/wood-certification';
import { WoodCertificationService } from '../proxy/geographic/controllers';
import { WOODCERTIFICATION_CONFIG, WOODCERTIFICATION_FIELDS } from './wood-certification.config';
import {
  BaseHierarchicalCrudComponent,
  DEFAULT_TOASTER_LIFE,
  GenericForm,
  IActionsSchema,
  ICrudService,
  resolveActions,
  ZnHierarchicalViewComponent,
} from '@zendra/shared';
import { finalize, Observable, tap } from 'rxjs';
import { WoodCertificationPlanningOrchestratorService } from 'projects/orchestrator/src/lib/proxy/orchestrator/app-services/wood-certification-planning-orchestrator.service';
import { WoodCertificationProjectionPlanningOrchestratorService } from 'orchestrator';
import { GetWoodCertificationProjectionListInputDto, WoodCertificationProjectionDto, WoodCertificationProjectionExcelDownloadDto } from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/wood-certifications/models';

@Component({
  selector: 'app-wood-certification',
  imports: [
    GenericForm,
    ZnHierarchicalViewComponent,
  ],
  providers: [ListService, WoodCertificationService, ConfirmationService],
  templateUrl: './wood-certification.html',
  styleUrl: './wood-certification.scss',
})
export class WoodCertification extends BaseHierarchicalCrudComponent<
  WoodCertificationProjectionDto,
  WoodCertificationCreateDto,
  WoodCertificationUpdateDto,
  WoodCertificationProjectionExcelDownloadDto
> implements OnInit {
  private readonly guardedDeletion = inject(GuardedDeletionService);
  private readonly geographicService = inject(WoodCertificationService);
  readonly orchestratorService = inject(WoodCertificationPlanningOrchestratorService);
  private readonly projectionService = inject(WoodCertificationProjectionPlanningOrchestratorService);
  readonly service: ICrudService<WoodCertificationProjectionDto, WoodCertificationCreateDto, WoodCertificationUpdateDto, WoodCertificationProjectionExcelDownloadDto> = {
    create: input => this.geographicService.create(input),
    update: (id, input) => this.geographicService.update(id, input),
    deleteByIds: ids => this.guardedDeletion.deleteByIds('wood-certifications', ids),
    getList: (query: GetWoodCertificationProjectionListInputDto) => this.projectionService.getList(query),
    getDownloadToken: () => this.projectionService.getDownloadToken(),
    getListAsExcelFile: input => this.projectionService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(this.projectionService, 'woodCertificationId'),
  };
  readonly config = WOODCERTIFICATION_CONFIG;
  readonly fields = WOODCERTIFICATION_FIELDS;
  actions: IActionsSchema | null = null;
  private isUpdatingCertifications = false;

  ngOnInit(): void {
    if (!this.config.actions || !this.config.actionIds) {
      return;
    }

    this.actions = this.withGridRefreshOnActionCompleted(
      resolveActions(this.config.actions, {
        [this.config.actionIds.updateCertificationTrigger]: () => this.updateCertifications(),
      }),
    );
  }

  protected prepareUpdateDto(data: any, selected: WoodCertificationProjectionDto): WoodCertificationUpdateDto {
    return {
      certificationCode: data.certificationCode,
      startDate: data.startDate,
      finishDate: data.finishDate,
      notes: data.notes,
      initialArea: data.initialArea,
      certificationTypeId: data.certificationTypeId,
      managementTypeId: data.managementTypeId,
      certificationLevelId: data.certificationLevelId,
      certificateTypeId: data.certificateTypeId,
      woodStatementId: data.woodStatementId,
      certificatorId: data.certificatorId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'WoodCertification';
  }

  private updateCertifications(): Observable<unknown> | void {
    const ids = this.selectedRows;

    if (!ids?.length || this.isUpdatingCertifications) {
      return;
    }

    const certificationId = ids[0];
    this.isUpdatingCertifications = true;

    return this.orchestratorService.updateCertifications(certificationId, { skipHandleError: true }).pipe(
      tap({
        next: result => {
          const queuedMessage = result?.accepted?.message;
          if (queuedMessage?.trim()) {
            this.toaster.success(queuedMessage, '', { life: DEFAULT_TOASTER_LIFE });
            return;
          }

          this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
            life: DEFAULT_TOASTER_LIFE,
            messageLocalizationParams: [
              this.getUpdateCertificationsActionLabel(),
            ],
          });
        },
        error: error => {
          const message =
            error?.error?.error?.message ??
            error?.error?.message ??
            error?.message ??
            'Error desconocido';

          this.toaster.error(message, '', { life: DEFAULT_TOASTER_LIFE });
        },
      }),
      finalize(() => {
        this.isUpdatingCertifications = false;
      }),
    );
  }

  private getUpdateCertificationsActionLabel(): string {
    return this.localizationService.instant('Geographic::Action.Trigger.UpdateCertifications');
  }
}
